import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { StationInventoryItem, StationInventoryTotals } from '@/lib/api/types';
import { fmtInt, initials } from '@/features/insights/engine';
import { SEGMENT_COLORS, TRACK_COLOR } from '@/features/insights/charts/chartTokens';
import { tileOf } from '@/features/insights/charts/PremiumsDelivered';
import { useScrollRamp } from '@/features/insights/motion/useScrollRamp';

/**
 * El merch de la estación con la gramática del informe: un anillo con lo
 * usado sobre lo asignado y, al lado, una fila por artículo con su barra.
 * Sustituye al donut y a las barras horizontales de Recharts, que eran otra
 * familia visual. Todo crece con la rampa al entrar en pantalla.
 */
export function StationMerch({ inventory, totals, className }: { inventory: StationInventoryItem[]; totals: StationInventoryTotals; className?: string }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const qty = totals.quantity || 1;
  const pct = Math.round((totals.used / qty) * 100);
  const filas = [...inventory].filter((it) => it.quantity > 0 || it.used > 0).sort((a, b) => b.used - a.used || b.quantity - a.quantity);

  if (totals.quantity === 0 && totals.used === 0) {
    return (
      <section className={cn('rounded-[14px] border p-6 text-center text-sm', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)', color: 'var(--color-dash-fg-muted)' }}>
        {t('stations.detail.dashboardEmpty')}
      </section>
    );
  }

  return (
    <section ref={ref} className={cn('flex flex-col gap-[18px] rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-3.5 basis-[300px]">
          <span className="text-[44px] font-bold leading-none tracking-[-0.025em] tabular-nums" style={{ color: 'var(--color-dash-olive)' }}>
            {fmtInt(Math.round(totals.used * p))}
          </span>
          <div className="flex min-w-0 flex-1 basis-[180px] flex-col gap-[3px]">
            <span className="text-[19px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{t('stations.detail.inventoryDonut')}</span>
            <span className="dash-mono text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('stations.dash.ofAllocated', { n: fmtInt(totals.quantity) })}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3.5 text-[12.5px]" style={{ color: 'var(--color-dash-fg-2)' }}>
          <span className="flex items-center gap-[7px]"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: SEGMENT_COLORS.delivered }} />{t('stations.detail.totalUsed')}</span>
          <span className="flex items-center gap-[7px]"><span className="h-2.5 w-2.5 rounded-[3px] border" style={{ background: TRACK_COLOR, borderColor: 'var(--color-dash-border)' }} />{t('stations.detail.totalRemaining')}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-6">
        {/* El anillo: lo usado sobre lo asignado */}
        <div className="flex min-w-[196px] basis-[232px] flex-col items-center gap-[13px]">
          <div className="relative h-[186px] w-[186px]">
            <div className="absolute inset-0 rounded-full" style={{ background: 'var(--color-dash-paper-3)' }} />
            <div className="absolute inset-[10px] rounded-full" style={{ background: `conic-gradient(${SEGMENT_COLORS.delivered} 0 ${(pct * p).toFixed(1)}%, transparent 0)` }} />
            <div className="absolute inset-9 flex flex-col items-center justify-center gap-[3px] rounded-full" style={{ background: 'var(--color-dash-paper)' }}>
              <span className="dash-mono text-[30px] font-bold leading-none tracking-[-0.02em]" style={{ color: 'var(--color-dash-fg)' }}>{Math.round(pct * p)}%</span>
              <span className="px-3.5 text-center text-[11px] leading-[1.3]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('stations.detail.totalUsed')}</span>
              <span className="dash-mono rounded-full px-2.5 py-[3px] text-xs font-bold" style={{ background: 'var(--color-dash-lime-soft)', color: 'var(--color-dash-olive)' }}>
                {t('stations.dash.rowUsed', { used: fmtInt(Math.round(totals.used * p)), total: fmtInt(totals.quantity) })}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-[11px]">
            {[
              { label: t('stations.detail.totalUsed'), value: fmtInt(totals.used), color: SEGMENT_COLORS.delivered },
              { label: t('stations.detail.totalRemaining'), value: fmtInt(totals.available), color: 'var(--color-dash-border)' },
            ].map((l) => (
              <span key={l.label} className="flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--color-dash-fg-muted)' }}>
                <span className="h-[9px] w-[9px] rounded-[2px]" style={{ background: l.color }} />
                {l.label}
                <span className="dash-mono font-bold" style={{ color: 'var(--color-dash-fg)' }}>{l.value}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Una fila por artículo */}
        <div className="flex min-w-[250px] flex-1 basis-[280px] flex-col gap-2">
          <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-olive)' }}>{t('stations.detail.stockDistribution')}</span>
          <div className="flex flex-col gap-[3px]">
            {filas.map((r, i) => {
              const tile = tileOf(i);
              const parte = r.quantity ? (r.used / r.quantity) * 100 : 0;
              const agotado = r.quantity > 0 && r.available === 0;
              return (
                <div key={r.id} className="flex items-center gap-2.5 rounded-[9px] border px-2.5 py-[9px]" style={{ borderColor: 'var(--color-dash-track)' }}>
                  {r.stockImage ? (
                    <img src={resolveImageUrl(r.stockImage)} alt="" className="h-7 w-7 shrink-0 rounded-[7px] object-cover" />
                  ) : (
                    <span className="dash-mono flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-[10.5px] font-bold" style={{ background: tile.bg, color: tile.fg }}>{initials(r.stockName)}</span>
                  )}
                  <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[13.5px] font-semibold" style={{ color: 'var(--color-dash-fg)' }}>{r.stockName}</span>
                      {agotado && (
                        <span className="dash-mono rounded-full px-2 py-[2px] text-[9.5px] font-bold uppercase tracking-[0.06em]" style={{ background: 'var(--color-dash-orange-soft)', color: 'var(--color-dash-orange)' }}>{t('stations.dash.soldOut')}</span>
                      )}
                    </span>
                    <span className="relative h-2 overflow-hidden rounded" style={{ background: TRACK_COLOR }}>
                      <span className="absolute inset-y-0 left-0 rounded transition-[width] duration-[650ms]" style={{ width: `${parte * p}%`, background: agotado ? 'var(--color-dash-amber)' : SEGMENT_COLORS.delivered }} />
                    </span>
                  </span>
                  <span className="dash-mono shrink-0 text-xs tabular-nums" style={{ color: 'var(--color-dash-fg-muted)' }}>
                    <span className="font-bold" style={{ color: 'var(--color-dash-fg)' }}>{fmtInt(Math.round(r.used * p))}</span>
                    {' / '}
                    {fmtInt(r.quantity)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
